import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronRight, ExternalLink, FileText, Link as LinkIcon, MoveUp, MoveDown, Hash, Square } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { v4 as uuidv4 } from 'uuid';

const MenuEditor = ({ menu, pages, anchors = [], onUpdate }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const [editingItem, setEditingItem] = useState(null);

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const addMenuItem = (parentItems = null, parentPath = []) => {
    const newItem = {
      item_id: uuidv4(),
      label: 'New Item',
      link_type: 'internal',
      link_value: '',
      target: '_self',
      order: parentItems ? parentItems.length : (menu.items?.length || 0),
      children: [],
    };

    let updatedItems;
    if (parentPath.length === 0) {
      updatedItems = [...(menu.items || []), newItem];
    } else {
      updatedItems = addItemToPath([...(menu.items || [])], parentPath, newItem);
    }

    onUpdate({ ...menu, items: updatedItems });
    setEditingItem(newItem.item_id);
  };

  const addItemToPath = (items, path, newItem) => {
    if (path.length === 0) return [...items, newItem];
    
    return items.map(item => {
      if (item.item_id === path[0]) {
        if (path.length === 1) {
          return {
            ...item,
            children: [...(item.children || []), newItem]
          };
        }
        return {
          ...item,
          children: addItemToPath(item.children || [], path.slice(1), newItem)
        };
      }
      return item;
    });
  };

  const updateMenuItem = (itemId, updates, items = menu.items) => {
    const updatedItems = items.map(item => {
      if (item.item_id === itemId) {
        return { ...item, ...updates };
      }
      if (item.children?.length > 0) {
        return {
          ...item,
          children: updateMenuItem(itemId, updates, item.children)
        };
      }
      return item;
    });

    if (items === menu.items) {
      onUpdate({ ...menu, items: updatedItems });
    }
    return updatedItems;
  };

  const deleteMenuItem = (itemId, items = menu.items) => {
    const updatedItems = items
      .filter(item => item.item_id !== itemId)
      .map(item => ({
        ...item,
        children: item.children ? deleteMenuItem(itemId, item.children) : []
      }));

    if (items === menu.items) {
      onUpdate({ ...menu, items: updatedItems });
    }
    return updatedItems;
  };

  const moveItem = (itemId, direction, items = menu.items, parentItems = null) => {
    const itemIndex = items.findIndex(item => item.item_id === itemId);
    if (itemIndex === -1) {
      // Search in children
      const updatedItems = items.map(item => {
        if (item.children?.length > 0) {
          return {
            ...item,
            children: moveItem(itemId, direction, item.children, items)
          };
        }
        return item;
      });
      if (parentItems === null) {
        onUpdate({ ...menu, items: updatedItems });
      }
      return updatedItems;
    }

    const newIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
    if (newIndex < 0 || newIndex >= items.length) return items;

    const updatedItems = [...items];
    [updatedItems[itemIndex], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[itemIndex]];

    // Update order values
    updatedItems.forEach((item, idx) => {
      item.order = idx;
    });

    if (parentItems === null) {
      onUpdate({ ...menu, items: updatedItems });
    }
    return updatedItems;
  };

  const getDepth = (itemId, items = menu.items, depth = 0) => {
    for (const item of items) {
      if (item.item_id === itemId) return depth;
      if (item.children?.length > 0) {
        const childDepth = getDepth(itemId, item.children, depth + 1);
        if (childDepth !== -1) return childDepth;
      }
    }
    return -1;
  };

  const renderMenuItem = (item, path = [], depth = 0) => {
    const isExpanded = expandedItems[item.item_id];
    const hasChildren = item.children?.length > 0;
    const isEditing = editingItem === item.item_id;
    const canAddChildren = depth < 2; // Max 3 levels (0, 1, 2)

    return (
      <div key={item.item_id} className="mb-2">
        <div
          className={`flex items-center gap-2 p-3 rounded-lg border ${
            isEditing ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'
          }`}
          style={{ marginLeft: depth * 24 }}
        >
          <div className="flex items-center gap-1 text-slate-400">
            <GripVertical size={16} className="cursor-grab" />
            <div className="flex flex-col">
              <button
                onClick={() => moveItem(item.item_id, 'up')}
                className="hover:text-slate-600 p-0.5"
              >
                <MoveUp size={12} />
              </button>
              <button
                onClick={() => moveItem(item.item_id, 'down')}
                className="hover:text-slate-600 p-0.5"
              >
                <MoveDown size={12} />
              </button>
            </div>
          </div>

          {hasChildren && (
            <button
              onClick={() => toggleExpanded(item.item_id)}
              className="p-1 hover:bg-slate-200 rounded"
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}

          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input
                      value={item.label}
                      onChange={(e) => updateMenuItem(item.item_id, { label: e.target.value })}
                      placeholder="Menu item label"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Link Type</Label>
                    <Select
                      value={item.link_type}
                      onValueChange={(value) => updateMenuItem(item.item_id, { link_type: value, link_value: '' })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">
                          <span className="flex items-center gap-2">
                            <FileText size={14} /> Internal Page
                          </span>
                        </SelectItem>
                        <SelectItem value="external">
                          <span className="flex items-center gap-2">
                            <ExternalLink size={14} /> External URL
                          </span>
                        </SelectItem>
                        <SelectItem value="anchor">
                          <span className="flex items-center gap-2">
                            <Hash size={14} /> Anchor Link
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">
                      {item.link_type === 'internal' ? 'Select Page' : item.link_type === 'anchor' ? 'Anchor ID' : 'External URL'}
                    </Label>
                    {item.link_type === 'internal' ? (
                      <Select
                        value={item.link_value}
                        onValueChange={(value) => updateMenuItem(item.item_id, { link_value: value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Select a page" />
                        </SelectTrigger>
                        <SelectContent>
                          {pages.map((page) => (
                            <SelectItem key={page.page_id} value={page.page_id}>
                              {page.title || page.slug}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : item.link_type === 'anchor' ? (
                      <div className="space-y-2">
                        {anchors.length > 0 ? (
                          <Select
                            value={item.link_value?.replace(/^#/, '').replace(/^[^#]*#/, '') || ''}
                            onValueChange={(value) => {
                              // Find the anchor to get its page slug
                              const selectedAnchor = anchors.find(a => a.id === value);
                              // Store as "pageSlug#anchorId" if anchor has a page, otherwise just "anchorId"
                              const linkValue = selectedAnchor?.pageSlug 
                                ? `${selectedAnchor.pageSlug}#${value}`
                                : value;
                              updateMenuItem(item.item_id, { link_value: linkValue });
                            }}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Select an anchor" />
                            </SelectTrigger>
                            <SelectContent>
                              {anchors.map((anchor, idx) => (
                                <SelectItem key={`${anchor.id}-${idx}`} value={anchor.id}>
                                  <span className="flex items-center gap-2">
                                    <Hash size={14} className="text-indigo-500" />
                                    <span className="truncate max-w-[150px]">{anchor.name}</span>
                                    <span className="text-slate-400 text-xs">#{anchor.id}</span>
                                    {anchor.source && (
                                      <span className="text-indigo-400 text-[10px] ml-1">({anchor.source})</span>
                                    )}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex">
                            <span className="inline-flex items-center px-3 text-sm text-slate-500 bg-slate-100 border border-r-0 border-slate-300 rounded-l-md">
                              #
                            </span>
                            <Input
                              value={item.link_value?.replace(/^#/, '') || ''}
                              onChange={(e) => updateMenuItem(item.item_id, { link_value: e.target.value.replace(/^#/, '') })}
                              placeholder="page-slug#section-id or just section-id"
                              className="h-8 rounded-l-none"
                            />
                          </div>
                        )}
                        <p className="text-xs text-slate-500">
                          {anchors.length > 0 
                            ? 'Select a section to link to. The link will include the page so it works from any page.'
                            : 'Enter "page-slug#section-id" to link to a specific page, or just "section-id" for current page'
                          }
                        </p>
                      </div>
                    ) : (
                      <Input
                        value={item.link_value}
                        onChange={(e) => updateMenuItem(item.item_id, { link_value: e.target.value })}
                        placeholder="https://example.com"
                        className="h-8"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Open in</Label>
                    <Select
                      value={item.target}
                      onValueChange={(value) => updateMenuItem(item.item_id, { target: value })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_self">Same Tab</SelectItem>
                        <SelectItem value="_blank">New Tab</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Button display toggle */}
                <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                  <Switch
                    checked={item.is_button || false}
                    onCheckedChange={(v) => updateMenuItem(item.item_id, { is_button: v })}
                    data-testid={`nav-item-button-toggle-${item.item_id}`}
                  />
                  <Label className="text-xs font-medium cursor-pointer">Display as Button</Label>
                </div>

                {item.is_button && (
                  <div className="grid grid-cols-2 gap-3 p-2.5 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div>
                      <Label className="text-xs">Button Style</Label>
                      <Select
                        value={item.button_style || 'solid'}
                        onValueChange={(v) => updateMenuItem(item.item_id, { button_style: v })}
                      >
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="solid">Solid (filled)</SelectItem>
                          <SelectItem value="outline">Outline (bordered)</SelectItem>
                          <SelectItem value="ghost">Ghost (subtle)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Corner Radius</Label>
                      <Select
                        value={item.button_border_radius || 'rounded'}
                        onValueChange={(v) => updateMenuItem(item.item_id, { button_border_radius: v })}
                      >
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="square">Square</SelectItem>
                          <SelectItem value="rounded">Rounded</SelectItem>
                          <SelectItem value="pill">Pill</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Background Color</Label>
                      <div className="flex gap-1">
                        <input
                          type="color"
                          value={item.button_bg_color || '#4f46e5'}
                          onChange={(e) => updateMenuItem(item.item_id, { button_bg_color: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer border border-slate-300"
                        />
                        <Input
                          value={item.button_bg_color || ''}
                          onChange={(e) => updateMenuItem(item.item_id, { button_bg_color: e.target.value })}
                          placeholder="Accent color"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Text Color</Label>
                      <div className="flex gap-1">
                        <input
                          type="color"
                          value={item.button_text_color || '#ffffff'}
                          onChange={(e) => updateMenuItem(item.item_id, { button_text_color: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer border border-slate-300"
                        />
                        <Input
                          value={item.button_text_color || ''}
                          onChange={(e) => updateMenuItem(item.item_id, { button_text_color: e.target.value })}
                          placeholder="#ffffff"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button size="sm" onClick={() => setEditingItem(null)}>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {item.link_type === 'external' ? (
                  <ExternalLink size={14} className="text-slate-400" />
                ) : item.link_type === 'anchor' ? (
                  <Hash size={14} className="text-slate-400" />
                ) : (
                  <FileText size={14} className="text-slate-400" />
                )}
                <span className="font-medium truncate">{item.label}</span>
                {item.is_button && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded font-medium uppercase tracking-wide">Button</span>
                )}
                {item.link_type === 'internal' && item.link_value && (
                  <span className="text-xs text-slate-400 truncate">
                    → {pages.find(p => p.page_id === item.link_value)?.slug || 'Unknown page'}
                  </span>
                )}
                {item.link_type === 'external' && item.link_value && (
                  <span className="text-xs text-slate-400 truncate">
                    → {item.link_value}
                  </span>
                )}
                {item.link_type === 'anchor' && item.link_value && (
                  <span className="text-xs text-slate-400 truncate">
                    → {item.link_value.includes('#') ? `/${item.link_value}` : `#${item.link_value}`}
                  </span>
                )}
              </div>
            )}
          </div>

          {!isEditing && (
            <div className="flex items-center gap-1">
              {canAddChildren && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    toggleExpanded(item.item_id);
                    addMenuItem(item.children, [...path, item.item_id]);
                  }}
                  className="h-7 px-2 text-slate-500"
                  title="Add sub-item"
                >
                  <Plus size={14} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingItem(item.item_id)}
                className="h-7 px-2 text-slate-500"
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteMenuItem(item.item_id)}
                className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          )}
        </div>

        {/* Render children */}
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {item.children.map((child) => renderMenuItem(child, [...path, item.item_id], depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-semibold">Menu Items</h3>
            <p className="text-sm text-slate-500">Drag to reorder, supports up to 3 levels of nesting</p>
          </div>
          <Button onClick={() => addMenuItem()} size="sm" className="gap-1">
            <Plus size={16} />
            Add Item
          </Button>
        </div>

        <div className="min-h-[200px]">
          {(!menu.items || menu.items.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-slate-200 rounded-lg">
              <LinkIcon size={32} className="text-slate-300 mb-2" />
              <p className="text-slate-500">No menu items yet</p>
              <p className="text-sm text-slate-400 mb-3">Click &quot;Add Item&quot; to create your first menu item</p>
              <Button onClick={() => addMenuItem()} size="sm" variant="outline">
                <Plus size={16} className="mr-1" />
                Add Item
              </Button>
            </div>
          ) : (
            <div>
              {menu.items.map((item) => renderMenuItem(item))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MenuEditor;
